import { useState, useEffect } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';

/**
 * Module-level cache so multiple consumers on a single page render (NavBar,
 * route guards, dashboard, child pages) share one /api/memberships/me request
 * instead of triggering one each. Keyed on user id — when a different user
 * signs in, the key changes and the cache misses, forcing a fresh fetch.
 *
 * The cache lives for the lifetime of the page (no auto-invalidation if a
 * moderator is added or removed mid-session); a page refresh re-syncs. If
 * mid-session staleness becomes a real problem, replace this with a context
 * provider that exposes an explicit refresh function.
 */
let cachedUserId = null;
let cachedPromise = null;

function fetchModeratorMemberships(api, userId) {
    if (cachedUserId === userId && cachedPromise) {
        return cachedPromise;
    }
    cachedUserId = userId;
    cachedPromise = api
        .get('/api/memberships/me')
        .then((response) =>
            response.data.filter(
                (m) => m.role === 'moderator' && m.status === 'active'
            )
        )
        .catch((err) => {
            // Clear the cache on failure so a subsequent mount can retry
            // instead of being stuck with a rejected promise.
            cachedUserId = null;
            cachedPromise = null;
            throw err;
        });
    return cachedPromise;
}

/**
 * Fetches the authenticated user's own community memberships and filters for
 * communities where their role is 'moderator'. Returns the list along with
 * loading/error state. Used by the ModeratorRoutes guard and moderator pages
 * to scope queries to only the communities this user moderates.
 *
 * @returns {{
 *   moderatorCommunities: Array<{_id, communityId, community: {_id, name, slug}, role, status}>,
 *   isModerator: boolean,
 *   loading: boolean,
 *   error: Error|null
 * }}
 */
export default function useModeratorCommunities() {
    const { user, profileLoading } = useAuth();
    const api = useAuthenticatedAxios();

    const [moderatorCommunities, setModeratorCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (profileLoading) return; // wait for auth — don't change loading state yet
        if (!user) {
            setLoading(false);
            setModeratorCommunities([]);
            return;
        }

        let cancelled = false;
        setLoading(true);

        fetchModeratorMemberships(api, user.uid)
            .then((moderated) => {
                if (cancelled) return;
                setModeratorCommunities(moderated);
                setError(null);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [user, profileLoading, api]);

    return {
        moderatorCommunities,
        isModerator: moderatorCommunities.length > 0,
        loading,
        error,
    };
}
